import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import { recordPendingRegistration } from '~/hooks/useIsPendingConfirmation';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { buildNotificationProducts } from '~/utils/browser';
import { getPageViewId } from '~/utils/navigationContext';
import { validateEmail } from '~/utils/validation';

import { Button } from '../../../Button';
import { authButtonClassName, authInputClassName } from '../AuthDialog';
import AuthPanel from '../AuthPanel';
import TermsNotice from '../TermsNotice';
import { type ScreenProps, Stage } from '.';

function ErrorMessage({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  return isActive ? (
    <div className="flex h-5 flex-row content-center items-center gap-1">
      <ErrorIcon />
      <p className="text-xs font-bold text-red-600">
        {t('login.emailAlreadyExistsError')}
      </p>
    </div>
  ) : (
    <></>
  );
}

type Props = ScreenProps & { setEmail: Dispatch<SetStateAction<string>> };

export default function SignInScreen({
  postAuthOptions,
  setStage,
  setEmail
}: Props) {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const { isBot } = useRootData();
  const { t } = useTranslation();

  const [emailValue, setEmailValue] = useState('');
  const isEmailValid = validateEmail(emailValue);

  const errors = fetcher.data?.errors;
  const success = fetcher.data?.success;
  const submittedEmail = fetcher.data?.email;
  const submitting = fetcher.state === 'submitting';

  useEffect(() => {
    if (!success) return;

    setEmail(submittedEmail!);
    setStage(Stage.Complete);

    if (postAuthOptions?.action === 'restock') {
      recordPendingRegistration(submittedEmail);
    }
  }, [success, submittedEmail, setEmail, setStage, postAuthOptions?.action]);

  const products = buildNotificationProducts(
    postAuthOptions?.productId,
    postAuthOptions?.sizes
  );

  return (
    <fetcher.Form
      action="/bolt/register/"
      className="flex w-full flex-col items-center gap-4"
      method="post"
    >
      <input type="hidden" value={JSON.stringify(products)} name="products" />
      <input type="hidden" value={postAuthOptions?.action} name="action" />
      <input type="hidden" value={user.guestId} name="guestId" />
      <input type="hidden" value={getPageViewId() ?? ''} name="viewId" />
      <AuthPanel>
        <TextInput
          backgroundTheme="light"
          className={authInputClassName}
          error={
            errors?.email ? 'This is an incorrect email format' : undefined
          }
          id="login-email"
          name="email"
          onChange={(e) => setEmailValue(e.currentTarget.value)}
          placeholder={t('login.emailAddress')}
          type="email"
          value={emailValue}
        />
        {emailValue && (
          <Button
            className={authButtonClassName}
            variant={isEmailValid ? 'forest' : 'disabled'}
            onClick={() =>
              new SignUpMethodEvent({
                buttonName: 'SIGN_UP_EMAIL',
                isBot,
                pagePosition: 'Sign in modal'
              }).send()
            }
          >
            {submitting ? t('login.sendingConfirmationEmail') : t('continue')}
          </Button>
        )}
        <ErrorMessage isActive={Boolean(errors?.conflict)} />
      </AuthPanel>
      <TermsNotice />
    </fetcher.Form>
  );
}
